import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Share2, RotateCcw, Trophy } from "lucide-react";

import Layout from "../components/Layout";
import Mascot from "../components/Mascot";
import { useAuth } from "../context/AuthContext";
import { api, apiErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { initState, step, flap as engineFlap, shoot as engineShoot, activatePowerup } from "../game/engine";
import { render as renderGame } from "../game/renderer";
import { shareScore } from "../game/shareCard";
import { useBonusVideo } from "../hooks/useBonusVideo";
import { Shield, Magnet, Timer } from "lucide-react";

const POWERUP_META = {
  shield: { icon: Shield, label: "Shield", color: "#00aaff" },
  magnet: { icon: Magnet, label: "Magnet", color: "#f7931a" },
  slowmo: { icon: Timer, label: "Slow-Mo", color: "#a36bff" },
};

// Preload sprites once at module scope so the RAF loop has them ready.
const MASCOT_IMG = typeof window !== "undefined" ? new Image() : null;
if (MASCOT_IMG) MASCOT_IMG.src = "/mascot_player.png?v=1";
const BRAND_IMG = typeof window !== "undefined" ? new Image() : null;
if (BRAND_IMG) BRAND_IMG.src = "/mascot.png?v=2";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Game() {
  const { user, refreshUser } = useAuth();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const nav = useNavigate();

  const [phase, setPhase] = useState("MENU");
  const [liveScore, setLiveScore] = useState(0);
  const [liveCoins, setLiveCoins] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [powerupBusy, setPowerupBusy] = useState(null);
  const [powerupsUsed, setPowerupsUsed] = useState({});
  const bonus = useBonusVideo({ refreshUser });

  // ---------- score submission ----------
  const submitScore = useCallback(async (s) => {
    setSubmitting(true);
    try {
      const sessionDuration = (performance.now() - s.startedAt) / 1000;
      const payload = {
        score: Math.floor(s.score),
        distance: Math.floor(s.distance),
        blocks_destroyed: s.blocksDestroyed,
        coins_collected: s.coinsCollected,
        session_duration: Number(sessionDuration.toFixed(2)),
        client_seed: s.clientSeed,
      };
      const { data } = await api.post("/games/submit", payload);
      if (data.ok) {
        track("game_submit", {
          score: payload.score,
          distance: payload.distance,
          blocks: payload.blocks_destroyed,
          bc_earned: data.bc_earned,
          personal_record: !!data.is_personal_record,
        });
        toast.success(`+${data.bc_earned} BC${data.is_personal_record ? " · NEW RECORD" : ""}`);
        setResult({
          score: payload.score,
          distance: payload.distance,
          blocks: payload.blocks_destroyed,
          coins: payload.coins_collected,
          bc_earned: data.bc_earned,
          is_personal_record: data.is_personal_record,
        });
        if (data.bonus_video) {
          bonus.setBonusVideo(data.bonus_video);
          toast.info(
            `🎁 Bonus unlocked! Watch the video for +${data.bonus_video.reward} BC`,
            { duration: 4500 },
          );
        }
        await refreshUser();
      } else {
        toast.error(`Game flagged: ${data.anticheat_flags?.join(", ") || "invalid"}`);
        setResult({
          score: payload.score,
          distance: payload.distance,
          blocks: payload.blocks_destroyed,
          coins: payload.coins_collected,
          bc_earned: 0,
          flagged: true,
        });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }, [bonus, refreshUser]);

  // ---------- start / input ----------
  const start = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    stateRef.current = initState(canvas.width, canvas.height);
    setPhase("PLAYING");
    setLiveScore(0);
    setLiveCoins(0);
    setResult(null);
    setPowerupsUsed({});
  }, []);

  const flap = useCallback(() => engineFlap(stateRef.current), []);
  const shoot = useCallback((x, y) => engineShoot(stateRef.current, x, y), []);

  const usePowerup = useCallback(async (kind) => {
    if (phase !== "PLAYING" || powerupsUsed[kind] || powerupBusy) return;
    const state = stateRef.current;
    if (!state || !state.alive) return;
    const stock = user?.powerups?.[kind] ?? 0;
    if (stock <= 0) {
      toast.info("You need to buy this power-up first");
      return;
    }
    setPowerupBusy(kind);
    try {
      await api.post("/powerups/use", { item_id: kind });
      const ok = activatePowerup(state, kind);
      if (!ok) throw new Error("activation_failed");
      setPowerupsUsed((prev) => ({ ...prev, [kind]: true }));
      toast.success(`${POWERUP_META[kind].label} activated!`);
      await refreshUser();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setPowerupBusy(null);
    }
  }, [phase, powerupsUsed, powerupBusy, user, refreshUser]);

  const onCanvasPointer = (e) => {
    if (phase !== "PLAYING") return;
    const rect = canvasRef.current.getBoundingClientRect();
    flap();
    shoot(e.clientX - rect.left, e.clientY - rect.top);
  };

  // Keyboard: Space flaps or starts the game.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space") return;
      e.preventDefault();
      if (phase === "MENU") start();
      else if (phase === "PLAYING") flap();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, flap, start]);

  // ---------- RAF loop ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min(40, now - last);
      last = now;
      const s = stateRef.current;
      if (s && phase === "PLAYING") {
        step(s, dt);
        renderGame(ctx, s, MASCOT_IMG);
        setLiveScore(Math.floor(s.score));
        setLiveCoins(s.coinsCollected);
        if (!s.alive && s.deadAt && now - s.deadAt > 900) {
          setPhase("GAME_OVER");
          submitScore(s);
        }
      } else if (s) {
        renderGame(ctx, s, MASCOT_IMG);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, submitScore]);

  // ---------- canvas resize ----------
  useEffect(() => {
    const resize = () => {
      const c = canvasRef.current;
      const box = containerRef.current;
      if (!c || !box) return;
      const rect = box.getBoundingClientRect();
      c.width = Math.floor(rect.width);
      c.height = Math.floor(rect.height);
      if (!stateRef.current) {
        stateRef.current = initState(c.width, c.height);
      } else {
        stateRef.current.W = c.width;
        stateRef.current.H = c.height;
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const onShare = () =>
    shareScore({
      result,
      user,
      brandImg: BRAND_IMG,
      refreshUser,
      backendUrl: BACKEND_URL,
    });

  return (
    <Layout>
      <div className="px-3 py-3 flex flex-col gap-3" data-testid="game-page">
        <HUD live={liveScore} coins={liveCoins} best={user?.personal_best ?? 0} />
        <div
          ref={containerRef}
          className="relative w-full rounded-[8px] overflow-hidden"
          style={{
            aspectRatio: "9/13",
            border: "2px solid rgba(46,117,182,0.6)",
            boxShadow:
              "inset 0 0 25px rgba(46,117,182,0.35), 0 0 22px rgba(46,117,182,0.35)",
          }}
        >
          <canvas
            ref={canvasRef}
            onPointerDown={onCanvasPointer}
            className="w-full h-full block"
            data-testid="game-canvas"
          />
          {phase === "MENU" && <MenuOverlay onStart={start} />}
          {phase === "GAME_OVER" && (
            <GameOverOverlay
              submitting={submitting}
              result={result}
              onShare={onShare}
              onReplay={start}
              onLeaderboard={() => nav("/leaderboard")}
            />
          )}
          {bonus.bonusVideo && phase === "GAME_OVER" && (
            <BonusVideoOverlay
              bonusVideo={bonus.bonusVideo}
              watch={bonus.bonusWatch}
              claiming={bonus.bonusClaiming}
              onClaim={bonus.claim}
              onSkip={bonus.skip}
            />
          )}
          {phase === "PLAYING" && (
            <PowerupTray
              powerups={user?.powerups || {}}
              used={powerupsUsed}
              busy={powerupBusy}
              onUse={usePowerup}
            />
          )}
        </div>
        <p className="text-center text-[10px] text-[#8b95a9] mt-1">
          TAP flaps AND shoots a bolt toward your tap. Destructible crypto
          blocks give +10 pts. Gray = death. Every 5 games → bonus video!
        </p>
      </div>
    </Layout>
  );
}

// -----------------------------------------------------------------------------
// Sub-components — kept small and side-effect-free
// -----------------------------------------------------------------------------
function HUD({ live, coins, best }) {
  return (
    <div className="flex items-center justify-between">
      <div className="cb-pill">
        <span className="text-[#2ecc71] text-glow-green">SCORE {live}</span>
      </div>
      <div className="cb-pill border-[rgba(247,147,26,0.45)] bg-[rgba(247,147,26,0.06)]">
        <span className="text-[#f7931a] text-glow-gold">¢ {coins}</span>
      </div>
      <div className="cb-pill border-[rgba(46,117,182,0.5)] bg-[rgba(46,117,182,0.06)]">
        <span className="text-[#2e75b6]">BEST {best}</span>
      </div>
    </div>
  );
}

function MenuOverlay({ onStart }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-[rgba(15,15,30,0.78)]">
      <Mascot size={110} />
      <div className="text-center">
        <div className="font-pixel text-[14px] text-glow-green">READY?</div>
        <p className="text-[11px] text-white/70 mt-2 max-w-[220px]">
          TAP / SPACE to flap. Aim your TAP at a block to shoot it. Gray blocks
          kill you — avoid them.
        </p>
      </div>
      <button onClick={onStart} data-testid="game-start-btn" className="cb-neon-btn">
        ▶ Start
      </button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-pixel text-[9px] text-[#8b95a9]">{label}</span>
      <span className="font-mono text-[12px] text-white">{value}</span>
    </div>
  );
}

function GameOverOverlay({ submitting, result, onShare, onReplay, onLeaderboard }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[rgba(15,15,30,0.88)] px-5">
      <h2
        className="font-pixel text-[14px] text-[#ff6b00] text-glow-gold"
        data-testid="gameover-title"
      >
        GAME OVER
      </h2>
      {submitting && (
        <div className="text-[11px] text-white/70">Submitting score...</div>
      )}
      {result && (
        <div
          className="w-full max-w-[260px] cb-card p-4 flex flex-col gap-2"
          data-testid="gameover-results"
        >
          <Row label="SCORE" value={result.score} />
          <Row label="DISTANCE" value={`${result.distance}m`} />
          <Row label="BLOCKS" value={result.blocks} />
          <Row label="COINS" value={result.coins} />
          <div className="h-px bg-[rgba(46,204,113,0.3)] my-1" />
          <div className="flex items-center justify-between">
            <span className="font-pixel text-[9px] text-[#f7931a]">BC EARNED</span>
            <span
              className="font-pixel text-[14px] text-[#f7931a] text-glow-gold"
              data-testid="gameover-bc"
            >
              +{result.bc_earned}
            </span>
          </div>
          {result.is_personal_record && (
            <div className="text-[10px] text-center text-[#2ecc71] text-glow-green mt-1">
              ★ NEW PERSONAL RECORD ★
            </div>
          )}
          {result.flagged && (
            <div className="text-[10px] text-center text-[#ff6b00]">
              Anticheat flagged this run.
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2 w-full max-w-[260px]">
        <button
          onClick={onShare}
          data-testid="gameover-share-btn"
          disabled={!result}
          className="cb-neon-btn orange"
        >
          <Share2 size={12} className="inline mr-2" /> Share Score
        </button>
        <button
          onClick={onReplay}
          data-testid="gameover-replay-btn"
          className="cb-neon-btn"
        >
          <RotateCcw size={12} className="inline mr-2" /> Play Again
        </button>
        <button
          onClick={onLeaderboard}
          data-testid="gameover-leaderboard-btn"
          className="cb-neon-btn blue"
        >
          <Trophy size={12} className="inline mr-2" /> Leaderboard
        </button>
      </div>
    </div>
  );
}

function BonusVideoOverlay({ bonusVideo, watch, claiming, onClaim, onSkip }) {
  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-[rgba(15,15,30,0.96)] px-4"
      data-testid="bonus-video-modal"
    >
      <div className="cb-pill border-[rgba(247,147,26,0.6)] text-[#f7931a] bg-[rgba(247,147,26,0.08)]">
        🎁 {bonusVideo.games_played_total}-GAMES BONUS
      </div>
      <h3
        className="font-pixel text-[12px] text-glow-gold text-center"
        data-testid="bonus-video-title"
      >
        WATCH & EARN +{bonusVideo.reward} BC
      </h3>
      <p className="text-[10px] text-[#8b95a9] text-center max-w-[260px] leading-snug">
        {bonusVideo.title}
      </p>
      <div className="w-full max-w-[300px] aspect-video rounded-[8px] overflow-hidden border border-[rgba(247,147,26,0.5)]">
        <iframe
          title={bonusVideo.title}
          src={`https://www.youtube.com/embed/${bonusVideo.youtube_id}?autoplay=1&rel=0`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="bonus-video-iframe"
        />
      </div>
      <div className="text-[10px] text-[#8b95a9] font-mono">
        {watch}s / min {bonusVideo.min_watch_seconds}s
      </div>
      <div className="flex gap-2 w-full max-w-[300px]">
        <button
          onClick={onSkip}
          data-testid="bonus-skip-btn"
          className="cb-neon-btn blue flex-1 text-[9px] py-2"
        >
          Skip
        </button>
        <button
          onClick={onClaim}
          disabled={claiming || watch < bonusVideo.min_watch_seconds}
          data-testid="bonus-claim-btn"
          className="cb-neon-btn orange flex-1 text-[9px] py-2"
        >
          {claiming ? "..." : `Claim +${bonusVideo.reward} BC`}
        </button>
      </div>
    </div>
  );
}

function PowerupTray({ powerups, used, busy, onUse }) {
  const entries = Object.entries(POWERUP_META);
  return (
    <div
      className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-auto"
      data-testid="powerup-tray"
    >
      {entries.map(([key, meta]) => {
        const count = powerups?.[key] ?? 0;
        const isUsed = !!used[key];
        const disabled = count <= 0 || isUsed || busy === key;
        const Icon = meta.icon;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onUse(key)}
            disabled={disabled}
            data-testid={`powerup-btn-${key}`}
            title={`${meta.label} · ${count} in stock`}
            className="relative w-12 h-12 rounded-lg flex items-center justify-center border-2 transition-all"
            style={{
              background: disabled ? "rgba(20,25,35,0.8)" : "rgba(15,15,30,0.9)",
              borderColor: isUsed || count <= 0 ? "rgba(255,255,255,0.12)" : meta.color,
              boxShadow: !disabled ? `0 0 10px ${meta.color}55` : "none",
              opacity: isUsed || count <= 0 ? 0.4 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <Icon size={18} style={{ color: meta.color }} />
            <span
              className="absolute -top-1 -right-1 text-[10px] font-pixel px-1 rounded-full"
              style={{
                background: meta.color,
                color: "#0f0f1e",
                minWidth: 16,
                textAlign: "center",
              }}
              data-testid={`powerup-count-${key}`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

